import { useFetch } from "hooks/api";
import React, { useMemo } from "react";
import { Row, Col, Table, Badge } from "react-bootstrap";
import Loading from "components/core/blocks/Loading";
import { formatCurrency, formatNumber } from "utils/formatNumber";
import { IoPricetagsSharp } from "react-icons/io5";
import { trellisPalette } from "components/custom/analytics/palettes";
import BuyBoxChart from "./BuyBoxChart";
import moment from "moment";
import { useDates } from "dates/DatesContext";
import { keepPreviousData } from "@tanstack/react-query";
import { useAppSelector } from "../../../redux/hooks";

const BuyBoxOffers = ({ asin }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { start, end } = useDates();

  const s = start !== undefined ? start : moment().subtract(30, "days");
  const e = end !== undefined ? end : moment();

  const { data, isLoading } = useFetch(
    ["buy_box_offer", marketPlace.marketPlace, asin],
    `/pricing/buybox/offer?asin=${asin}&market_place=${marketPlace.marketPlace}`,
    {},
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const { data: percentageData, isLoading: percentageLoading } = useFetch(
    ["buy_box_percentage", marketPlace.marketPlace, asin],
    `/pricing/buybox/percentage`,
    {
      asin: asin,
      market_place: marketPlace.marketPlace,
      start_date: s.format("YYYY-MM-DD"),
      end_date: e.format("YYYY-MM-DD"),
    },
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const buyBoxPrice = useMemo(() => {
    if (data?.Summary?.BuyBoxPrices?.length > 0) {
      for (let i = 0; i < data?.Summary?.BuyBoxPrices?.length; i++) {
        const bb = data?.Summary?.BuyBoxPrices[i];
        if (bb?.condition === "New") {
          return bb?.ListingPrice?.Amount;
        }
      }
    }

    return null;
  }, [data]);

  if (isLoading || percentageLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <Row className="p-3">
      <BuyBoxChart percentageData={percentageData} />

      <Col xs={12}>
        <Row className="fs-5 text-center pb-3">
          <Col xs={12} className="text-start fs-5">
            <div
              style={{
                padding: "2rem",
                border: "1px solid rgb(238, 238, 238)",
                borderRadius: "6px",
                backgroundColor: "rgb(248, 248, 248)",
                marginTop: "10px",
              }}
            >
              <div>
                The following table shows the current offers for the ASIN: {asin} which are available in the Buy Box.
                Offers that have the{" "}
                <span style={{ color: trellisPalette[0], fontWeight: "bold" }}>
                  <IoPricetagsSharp />
                </span>{" "}
                icon are from the your own amazon store.
              </div>
            </div>
          </Col>
        </Row>
        <Table
          borderless
          hover
          size="sm"
          className="fs-4"
          style={{
            minWidth: "900px",
            maxWidth: "1600px",
            margin: "0 auto",
          }}
        >
          <thead>
            <tr>
              <th className="text-start">Seller Id</th>
              <th className="text-end">Price</th>
              <th className="text-end">Shipping</th>
              <th className="text-end">Feedback Cnt</th>
              <th className="text-end">Feedback Rtg</th>
              <th className="text-center">Condition</th>
              <th className="text-center">Is Featured Merchant</th>
              <th className="text-center">Is BuyBox Winner</th>
              <th className="text-center">Is FBA</th>
            </tr>
          </thead>
          {data?.Offers?.map((offer, i) => (
            <tbody key={`${offer.SellerId}-${offer.ListingPrice.Amount}-${i}`}>
              <tr style={{ backgroundColor: data?.your_seller_id === offer.SellerId ? "#f0f0f0" : "white" }}>
                <td className="text-start">
                  <a href={`https://www.amazon.com/sp?seller=${offer.SellerId}`} target="_blank" rel="noreferrer">
                    {offer.SellerId}
                  </a>
                  {data?.your_seller_id === offer.SellerId && (
                    <span style={{ color: trellisPalette[0], fontWeight: "bold" }}>
                      {" "}
                      <IoPricetagsSharp />{" "}
                    </span>
                  )}
                </td>
                <td className="text-end">{formatCurrency(offer?.ListingPrice?.Amount, marketPlace.marketPlace)}</td>
                <td className="text-end">{formatCurrency(offer?.Shipping?.Amount, marketPlace.marketPlace)}</td>
                <td className="text-end">
                  {formatNumber(offer?.SellerFeedbackRating?.FeedbackCount, { decimalPlaces: 0 })}
                </td>
                <td className="text-end">{offer?.SellerFeedbackRating?.SellerPositiveFeedbackRating}</td>
                <td className="text-center">{offer?.SubCondition}</td>
                <td className="text-center">{offer?.IsFeaturedMerchant ? "Yes" : "No"}</td>
                <td className="text-center">
                  {offer?.IsBuyBoxWinner && buyBoxPrice === offer?.ListingPrice?.Amount ?
                    <Badge className="bg-trellis-green" pill>
                      Yes
                    </Badge>
                  : <Badge className="badge bg-trellis-fuchsia" pill>
                      No
                    </Badge>
                  }
                </td>
                <td className="text-center">{offer?.IsFulfilledByAmazon ? "Yes" : "No"}</td>
              </tr>
            </tbody>
          ))}

          {data?.Offers?.length === 0 && (
            <tbody>
              <tr>
                <td colSpan="8" className="py-5 text-center">
                  <strong>There are no offers for this product in the Buy Box</strong>
                </td>
              </tr>
            </tbody>
          )}
        </Table>
      </Col>
    </Row>
  );
};

export default BuyBoxOffers;
